import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, Loader2, Mail, Phone, MessageCircle, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useSettings } from '../context/SettingsContext';

const inputCls =
  'bg-white/5 border-white/15 text-white placeholder:text-white/45 focus-visible:ring-[#00b4d8] focus-visible:ring-offset-0 rounded-xl';

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '', kind: 'complaint' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Please enter your name.');
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return setError('Please enter a valid email.');
    if (!form.message.trim()) return setError('Please share what you would like to tell us.');
    setSubmitting(true);
    try {
      const { data } = await api.post('/feedback/complaint', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim() || 'Customer message',
        message: form.message.trim(),
        kind: form.kind,
      });
      setSent({ ref: data?.ticket_ref });
      setForm({ name: '', email: '', phone: '', subject: '', message: '', kind: 'complaint' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send your message. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not send your message.');
    } finally {
      setSubmitting(false);
    }
  };

  const inboxConfigured = settings?.has_complaints_inbox !== false;

  return (
    <div className="pt-24 pb-20">
      <section className="relative">
        <div className="absolute inset-0 hero-radial pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">Talk to us</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
            Customer Voice
          </h1>
          <p className="mt-4 text-white/75 max-w-2xl text-base sm:text-lg leading-relaxed">
            Compliments, complaints, questions — whatever you have to share, our team reads every message and replies personally.
          </p>

          <div className="mt-10 grid lg:grid-cols-5 gap-6">
            <Card className="rounded-3xl border-white/10 bg-white/[0.04] backdrop-blur p-6 lg:col-span-2 space-y-3">
              <div className="font-display text-lg font-semibold">Other ways to reach us</div>
              {settings?.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-3 text-sm text-white/80 hover:text-white">
                  <span className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-white/5 border border-white/10"><Mail className="h-4 w-4 text-[#00b4d8]" /></span>
                  {settings.contact_email}
                </a>
              )}
              {settings?.contact_phone && (
                <a href={`tel:${settings.contact_phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-sm text-white/80 hover:text-white">
                  <span className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-white/5 border border-white/10"><Phone className="h-4 w-4 text-[#00b4d8]" /></span>
                  {settings.contact_phone}
                </a>
              )}
              {settings?.whatsapp_url && (
                <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-white/80 hover:text-white">
                  <span className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-[#25D366]/15 border border-[#25D366]/30"><MessageCircle className="h-4 w-4 text-[#25D366]" /></span>
                  WhatsApp
                </a>
              )}
              <div className="pt-3 border-t border-white/8 text-xs text-white/55">
                Replies come from our customer-voice mailbox and land directly in the inbox of the email you provide below.
              </div>
            </Card>

            <Card className="rounded-3xl border-white/10 bg-white/[0.04] backdrop-blur p-6 lg:col-span-3">
              {sent ? (
                <div data-testid="contact-success" className="rounded-2xl border border-[#c8e600]/30 bg-[#c8e600]/[0.08] p-5 flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-[#c8e600] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display text-lg font-semibold">Thanks — message received!</div>
                    <p className="mt-1 text-sm text-white/75">
                      Ticket <span className="font-mono text-[#c8e600]">{sent.ref}</span>. We will reply within a few hours.
                    </p>
                  </div>
                </div>
              ) : !inboxConfigured ? (
                <div data-testid="contact-not-configured" className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5 flex items-start gap-3 text-amber-100">
                  <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    The contact mailbox hasn't been configured by the site owner yet. Please reach us via phone or WhatsApp in the meantime.
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="kind" className="text-white/75 text-xs uppercase tracking-wider">Type</Label>
                    <Select value={form.kind} onValueChange={(v) => set('kind', v)}>
                      <SelectTrigger id="kind" data-testid="contact-kind" className={`${inputCls} mt-1.5 h-11`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#07101f] border-white/15 text-white">
                        <SelectItem value="complaint" className="focus:bg-white/10 focus:text-white">Complaint</SelectItem>
                        <SelectItem value="support" className="focus:bg-white/10 focus:text-white">Support / Question</SelectItem>
                        <SelectItem value="feedback" className="focus:bg-white/10 focus:text-white">Feedback / Suggestion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="name" className="text-white/75 text-xs uppercase tracking-wider">Name</Label>
                      <Input id="name" data-testid="contact-name" className={`${inputCls} mt-1.5 h-11 px-4`} value={form.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-white/75 text-xs uppercase tracking-wider">Phone (optional)</Label>
                      <Input id="phone" data-testid="contact-phone" className={`${inputCls} mt-1.5 h-11 px-4`} value={form.phone} onChange={(e) => set('phone', e.target.value)} type="tel" autoComplete="tel" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white/75 text-xs uppercase tracking-wider">Email</Label>
                    <Input id="email" data-testid="contact-email" className={`${inputCls} mt-1.5 h-11 px-4`} value={form.email} onChange={(e) => set('email', e.target.value)} type="email" autoComplete="email" />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-white/75 text-xs uppercase tracking-wider">Subject (optional)</Label>
                    <Input id="subject" data-testid="contact-subject" className={`${inputCls} mt-1.5 h-11 px-4`} value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="Short summary" />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white/75 text-xs uppercase tracking-wider">Message</Label>
                    <Textarea id="message" data-testid="contact-message" rows={6} className={`${inputCls} mt-1.5 px-4 py-3 resize-y`} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Tell us what is on your mind…" />
                  </div>

                  {error && (
                    <div data-testid="contact-error" className="rounded-xl border border-red-400/30 bg-red-400/10 text-red-200 text-sm px-4 py-2">{error}</div>
                  )}

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="w-full bg-[#0078d4] hover:bg-[#0066b8] text-white font-semibold rounded-xl h-12 text-base shadow-lg shadow-[#0078d4]/30 disabled:opacity-60"
                  >
                    {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</>) : 'Send message'}
                  </Button>
                  <p className="text-[11px] text-white/45 text-center">We reply directly to your email address — never shared with anyone.</p>
                </form>
              )}
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
